import React from 'react';
import { Check, Sparkles, Heart, ZoomIn } from 'lucide-react';
import { fabricIdentifier } from '../../../lib/fabricLabels';
import { StockDot } from '../../fabric/StockDot';
import { ColorImage } from '../../admin/fabric/ColorImage';
import { roomLabel } from '../../../lib/roomTypes';
import { brandLabel } from '../../../lib/brandNames';

export const FabricSwatchCard = ({ entry, view, roomType, isSelected, isActive, onClick, isFavorite, onToggleFavorite, onEnlarge }) => {
  const roomUrl = (roomType && entry.room_visualizations?.[roomType]) || entry.room_visualization_url || entry.ai_visualization_url;
  const roomMismatch = view === 'room' && roomType && roomUrl && !entry.room_visualizations?.[roomType];
  const src = view === 'room' ? roomUrl || entry.image_url : entry.image_url || roomUrl;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(entry)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(entry); } }}
      data-testid={`fabric-card-${entry.id}`}
      className={`group relative text-left rounded-xl overflow-hidden border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer ${
        isActive ? 'border-amber-500 ring-2 ring-amber-200' : 'border-neutral-200'
      }`}
    >
      <div className="relative aspect-square">
        <ColorImage src={src} alt={entry.name} hex={entry.hex_color} className="w-full h-full" />
        {onToggleFavorite && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(entry); }}
            aria-label={isFavorite ? 'Remove from shortlist' : 'Add to shortlist'}
            aria-pressed={isFavorite}
            data-testid={`fabric-card-heart-${entry.id}`}
            className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow transition-all ${
              isFavorite ? 'bg-rose-500 text-white opacity-100' : 'bg-white/90 text-neutral-600 opacity-0 group-hover:opacity-100 hover:text-rose-500'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        )}
        {isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center shadow" data-testid={`fabric-card-selected-${entry.id}`}>
            <Check className="w-3.5 h-3.5 text-white" />
          </div>
        )}
        {roomUrl && view === 'swatch' && (
          <div className="absolute bottom-2 left-2 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Room view available">
            <Sparkles className="w-3.5 h-3.5 text-amber-600" />
          </div>
        )}
        {onEnlarge ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEnlarge(entry); }}
            aria-label="Enlarge swatch"
            title="Enlarge"
            data-testid={`fabric-card-zoom-${entry.id}`}
            className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white/90 text-neutral-700 flex items-center justify-center shadow opacity-0 group-hover:opacity-100 hover:bg-amber-500 hover:text-white transition-all"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="absolute bottom-2 right-2 w-6 h-6 rounded-full border-2 border-white shadow" style={{ backgroundColor: entry.hex_color || '#ccc' }} />
        )}
        <StockDot status={entry.stock_status} discontinued={entry.discontinued} className="absolute bottom-2 left-2" testId={`fabric-card-stock-${entry.id}`} />
        {roomMismatch && (
          <span className="absolute bottom-2 left-7 text-[10px] px-1.5 py-0.5 rounded bg-black/60 text-white" data-testid={`fabric-card-room-fallback-${entry.id}`}>
            {roomLabel(entry.visualization_room_type)} view
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="font-medium text-sm truncate" title={entry.name}>{entry.name}{entry.code ? <span className="ml-1 font-mono text-[10px] text-neutral-400">{entry.code}</span> : null}</p>
        <p className="text-[11px] text-neutral-500 truncate" title={fabricIdentifier(entry)} data-testid={`fabric-card-path-${entry.id}`}>
          {[brandLabel(entry.brand_name), entry.collection_name, entry.material_name].filter(Boolean).join(' › ')}
        </p>
      </div>
    </div>
  );
};

export default FabricSwatchCard;
